import styled from '@emotion/styled'

import {
  ROW_METRICS,
  Stack,
  gridTemplate,
  hasTrailing,
  mutedText,
  rowBorder,
  rowFill,
} from './shared'

const HEADER_FONT_WEIGHT = 300
const HEADER_PADDING_BOTTOM = '8px'

const Table = styled.div`
  display: flex;
  flex-direction: column;
`

// Reserve the row border width so the header columns align with the row cells.
const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$template};
  column-gap: ${p => p.$columnGap};
  align-items: center;
  padding: 0 ${ROW_METRICS.inlinePadding} ${HEADER_PADDING_BOTTOM};
  border: 2px solid transparent;
  color: ${p => p.theme.mediumGray};
  font-size: 16px;
  font-weight: ${HEADER_FONT_WEIGHT};
`

const Row = styled.div`
  ${rowBorder};
  display: grid;
  grid-template-columns: ${p => p.$template};
  column-gap: ${p => p.$columnGap};
  align-items: center;
  min-height: ${ROW_METRICS.minHeight};
  padding: ${ROW_METRICS.blockPadding} ${ROW_METRICS.inlinePadding};
  border-radius: ${ROW_METRICS.radius};
  background: ${rowFill};
`

const HeaderCell = styled.div`
  min-width: 0;
  text-align: ${p => p.$align ?? 'left'};
`

const Cell = styled.div`
  min-width: 0;
  font-size: 16px;
  text-align: ${p => p.$align ?? 'left'};
  font-weight: ${p => (p.$emphasize ? 600 : 'normal')};
  color: ${mutedText};
`

const TrailingCell = styled.div`
  display: flex;
  justify-content: flex-end;
`

const DesktopDataTable = ({ columns, rows, className, trailingWidth, columnGap }) => {
  const showTrailing = hasTrailing(rows)
  const template = gridTemplate(columns, { hasTrailing: showTrailing, trailingWidth })

  return (
    <Table className={className}>
      <HeaderRow $template={template} $columnGap={columnGap}>
        {columns.map(col => (
          <HeaderCell key={col.key} $align={col.align}>
            {col.label}
          </HeaderCell>
        ))}
        {showTrailing && <div />}
      </HeaderRow>

      <Stack $gap={ROW_METRICS.gap}>
        {rows.map(row => (
          <Row
            key={row.id}
            id={row.domId}
            $template={template}
            $columnGap={columnGap}
            $muted={row.muted}>
            {columns.map((col, index) => (
              <Cell key={col.key} $align={col.align} $muted={row.muted} $emphasize={index === 0}>
                {row.values[col.key]}
              </Cell>
            ))}
            {showTrailing && <TrailingCell>{row.trailing}</TrailingCell>}
          </Row>
        ))}
      </Stack>
    </Table>
  )
}

export default DesktopDataTable
